import { FC, FormEvent, ReactElement } from 'react';

import { FormattedMessage } from 'react-intl';

import { useUserData } from 'context';
import { useSavedRemoval } from 'hooks/useSavedRemoval';
import { KnowledgeAreaApi, KnowledgeAreaLevelExercisesApi } from 'services';
import { HandleLocalState, VoidFunctionNoArgs } from 'types';
import { IKnowledgeAreaDetails } from 'types/domain/knowledgeArea';
import { Button, Modal, Text } from 'views/components';
import { IExercise } from 'views/components/Exercise/exercise.interface';
import { DialogFooter, DialogForm } from 'views/components/UI/Modal';
import { RecoveryNotice } from 'views/components/UI/RecoveryNotice';

interface IExerciseDeleteProps {
  isOpen: boolean;
  handleClose: VoidFunctionNoArgs;
  currentExercise: IExercise | null;
  handleExercises: HandleLocalState<IExercise[] | []>;
  knowledgeAreaId: number;
  levelId: number;
  handleKnowledgeAreaDetails: HandleLocalState<IKnowledgeAreaDetails | null>;
}

const ExerciseDeleteContent: FC<IExerciseDeleteProps> = (props): ReactElement => {
  const {
    isOpen,
    handleClose,
    currentExercise,
    handleExercises,
    knowledgeAreaId,
    levelId,
    handleKnowledgeAreaDetails,
  } = props;
  const recovery = useSavedRemoval(
    isOpen,
    ({
      exercises,
      details,
    }: {
      exercises: IExercise[];
      details: IKnowledgeAreaDetails;
    }) => {
      handleExercises([...exercises].sort((first, second) => first.id - second.id));
      handleKnowledgeAreaDetails(details);
      handleClose();
    },
    'ExerciseDelete.remove',
  );
  const { isLoading } = recovery;
  const remove = (): Promise<void> =>
    recovery.run(
      signal =>
        new KnowledgeAreaLevelExercisesApi().delete(currentExercise!.id, { signal }),
      signal => new KnowledgeAreaApi().updateModified(knowledgeAreaId, { signal }),
      async signal => {
        const [exercises, details] = await Promise.all([
          new KnowledgeAreaLevelExercisesApi().getAllExercisesByLevel(levelId, {
            signal,
          }),
          new KnowledgeAreaApi().getOneById(knowledgeAreaId, { signal }),
        ]);
        return { exercises, details };
      },
      () =>
        handleExercises(exercises =>
          exercises.filter(exercise => exercise.id !== currentExercise!.id),
        ),
    );
  function onFormSubmit(event: FormEvent): void {
    event.preventDefault();
    if (!recovery.needsRecovery) remove();
  }

  return (
    <Modal
      busy={isLoading}
      isOpen={isOpen}
      handleClose={handleClose}
      modalTitle="knowledge-area.exercise-delete.modal.title"
      size="medium"
    >
      <DialogForm onSubmit={onFormSubmit}>
        {recovery.needsRecovery && !isLoading && (
          <RecoveryNotice
            role="alert"
            mode="warning"
            action={{ onClick: remove, disabled: isLoading }}
          >
            <FormattedMessage id="removal.saved-refresh-error" />
          </RecoveryNotice>
        )}
        {!recovery.needsRecovery && recovery.errMessage && (
          <div role="alert">
            <Text color="var(--sk-text-danger)">{recovery.errMessage}</Text>
          </div>
        )}
        <Text>
          <FormattedMessage
            id="knowledge-area.exercise-delete.modal.description"
            values={{
              exerciseName: <b>{currentExercise!.name}</b>,
            }}
          />
        </Text>
        <DialogFooter>
          <Button
            type="submit"
            mode="secondary"
            color="danger"
            disabled={isLoading || recovery.needsRecovery}
          >
            <FormattedMessage id="knowledge-area.exercise-delete.modal.button.delete" />
          </Button>
        </DialogFooter>
      </DialogForm>
    </Modal>
  );
};

const ExerciseDelete: FC<IExerciseDeleteProps> = props => {
  const { user, currentOrganization } = useUserData();
  const { currentExercise, levelId, knowledgeAreaId } = props;
  return (
    <ExerciseDeleteContent
      key={`${knowledgeAreaId}:${levelId}:${currentExercise?.id}:${user?.id}:${user?.role.name}:${currentOrganization?.id}`}
      {...props}
    />
  );
};
export { ExerciseDelete };
